import { Box, HStack, Text, VStack, Badge, IconButton } from '@chakra-ui/react';
import { Avatar } from '@/components/ui/avatar';
import { Conversation } from '@/features/conversations/conversations.types';
import { useMakeConversationRead, useDeleteConversation } from '@/features/conversations/conversations.hook';
import { useCallback, useState } from 'react';
import { FaTrash } from 'react-icons/fa';
import {
  DialogRoot,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
  DialogCloseTrigger,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { toaster } from '@/components/ui/toaster';

interface ConversationItemProps {
  conversation: Conversation;
  isSelected: boolean;
  onClick: () => void;
  onDeleted?: () => void;
}

// Helper function to format timestamp
function getRelativeTime(timestamp: string): string {
  if (!timestamp) return '';

  const date = new Date(timestamp);
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (diffInSeconds < 60) return 'just now';
  if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m`;
  if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h `;
  if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d`;

  const diffInDays = Math.floor(diffInSeconds / 86400);
  if (diffInDays < 7) return `${diffInDays}d`;
  if (diffInDays < 30) return `${Math.floor(diffInDays / 7)}w`;
  if (diffInDays < 62) return `${Math.floor(diffInDays / 30)}mo`;

  // Format as date for older messages
  return date.toLocaleDateString();
}

export function ConversationItem({
  conversation,
  isSelected,
  onClick,
  onDeleted,
}: ConversationItemProps) {
  const { mutate: makeConversationRead } =
    useMakeConversationRead(conversation);
  const { mutate: deleteConversation, isPending: isDeleting } =
    useDeleteConversation();
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  // Get user name - either from user object or fallback to summary
  const userName = conversation.user
    ? `${conversation.user.first_name} ${conversation.user.last_name || ''}`.trim()
    : conversation.summary || 'Unknown';

  // Get user avatar
  const userAvatar = conversation.user?.avatar;
  const isUserMember = conversation.user?.isMember;

  // Get the last message content
  const lastMessageContent =
    conversation.lastMessage?.message_type === 'attachments'
      ? '📎 Media message'
      : conversation.lastMessage?.content || 'No messages yet';

  // Get relative time
  const relativeTime = conversation.lastMessage
    ? getRelativeTime(conversation.lastMessage.timestamp)
    : getRelativeTime(conversation.last_message_at);

  // Get unread count
  const unreadCount = conversation.unreadCount || 0;

  const handleClick = useCallback(() => {
    if (unreadCount > 0) {
      makeConversationRead({
        id: conversation.id,
        data: {
          is_read: true,
        },
      });
    }
    onClick();
  }, [unreadCount, conversation.id, onClick, makeConversationRead]);

  const handleDeleteClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      setIsDeleteDialogOpen(true);
    },
    [],
  );

  const handleConfirmDelete = useCallback(() => {
    deleteConversation(
      { id: conversation.id },
      {
        onSuccess: () => {
          toaster.create({
            title: 'Conversation deleted',
            description: 'The conversation has been deleted successfully.',
            type: 'success',
          });
          setIsDeleteDialogOpen(false);
          onDeleted?.();
        },
        onError: (error) => {
          toaster.create({
            title: 'Error',
            description: `Failed to delete conversation: ${error.message}`,
            type: 'error',
          });
        },
      },
    );
  }, [conversation.id, deleteConversation, onDeleted]);

  const agentStatus = conversation.has_agent_active ? 'active' : 'inactive';

  return (
    <>
      <Box
        as="button"
        onClick={handleClick}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        py={3}
        px={4}
        width="100%"
        textAlign="left"
        transition="background-color 0.2s"
        backgroundColor={isSelected ? 'gray.100' : 'transparent'}
        borderRadius="2xl"
        _hover={{
          backgroundColor: isSelected ? 'gray.100' : 'gray.50',
        }}
        borderBottom="1px"
        borderColor="gray.200"
        cursor="pointer"
        position="relative"
      >
        <HStack gap={3} align="flex-start">
          <Avatar
            size="md"
            name={userName}
            src={userAvatar}
            border={isUserMember ? '2px solid' : 'none'}
            borderColor={isUserMember ? 'green.500' : 'none'}
          />
          <VStack align="flex-start" gap={0.5} flex="1" overflow="hidden">
            <HStack justify="space-between" width="100%">
              <Text
                fontWeight={unreadCount > 0 ? 'bolder' : 'medium'}
                fontSize="md"
                color={isUserMember ? 'green.600' : 'gray.900'}
              >
                {userName}
                {agentStatus === 'active' && (
                  <Badge
                    as="span"
                    bg="green.400"
                    color="white"
                    borderRadius="full"
                    size="sm"
                    ml={2}
                  >
                    AI Active
                  </Badge>
                )}
              </Text>
              <HStack gap={2}>
                {isHovered && (
                  <IconButton
                    aria-label="Delete conversation"
                    size="xs"
                    variant="ghost"
                    colorPalette="red"
                    onClick={handleDeleteClick}
                  >
                    <FaTrash />
                  </IconButton>
                )}
                <Text
                  fontSize="xs"
                  color="gray.500"
                  fontWeight={unreadCount > 0 ? 'bold' : 'normal'}
                >
                  {relativeTime}
                </Text>
              </HStack>
            </HStack>
            <HStack width="100%" justify="space-between">
              <Text
                fontSize="sm"
                color={unreadCount > 0 ? 'gray.900' : 'gray.400'}
                fontWeight={unreadCount > 0 ? 'medium' : 'normal'}
                flex="1"
                truncate
              >
                {lastMessageContent}
              </Text>
              {unreadCount > 0 && (
                <Badge
                  borderRadius="full"
                  px="2"
                  colorScheme="blue"
                  fontSize="xs"
                >
                  {unreadCount}
                </Badge>
              )}
            </HStack>
          </VStack>
        </HStack>
      </Box>

      <DialogRoot
        open={isDeleteDialogOpen}
        onOpenChange={(e) => setIsDeleteDialogOpen(e.open)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Conversation</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <Text>
              Are you sure you want to delete this conversation with{' '}
              <strong>{userName}</strong>? This will permanently delete all
              messages and cannot be undone.
            </Text>
          </DialogBody>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsDeleteDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              colorPalette="red"
              onClick={handleConfirmDelete}
              loading={isDeleting}
            >
              Delete
            </Button>
          </DialogFooter>
          <DialogCloseTrigger />
        </DialogContent>
      </DialogRoot>
    </>
  );
}
